import { AppRegistrationSection } from 'AppRegistrationSection';
import { Button, Card, Link, Text } from 'components';
import { dateFormat, numberToCurrency } from 'utils/translate';
import { Frame } from 'components/organisms';
import { frameConfig } from 'AppConfig';
import { getFetchHeaders } from 'utils/fetchHeaders';
import { getScreenTranslation } from 'utils/translations';
import { ioBlackBoxAtom } from 'hooks/useIovation';
import { isProductKind } from 'utils/getProductNumber';
import { PRODUCT_NUMBER_IL } from 'utils/constants';
import { Response as ResponseOffer } from 'api/typegen/application/first-loan-offer/product-number/{productNumber}/get';
import { ROUTES } from 'utils/constants/constants_routes';
import { useAnalyticsCookies } from 'hooks/useAnalyticsCookies';
import { useAtomValue } from 'jotai/index';
import { useErrorManager } from 'InternalLibrary';
import { useGetClientApplicationProposals, usePutApplication } from 'api';
import { useNavigate } from 'react-router-dom';
import LegalTermsModal from 'components/atoms/LegalTermsModal/LegalTermsModal';
import Loader from 'components/Loader/Loader';
import Placeholder from 'components/Placeholder/Placeholder';
import useApplication from 'hooks/useApplication';
import useApplicationAndLexisNexisMutation from 'hooks/useApplicationAndLexisNexisMutation';
import useClientOffer from 'hooks/useClientOffer';

import { getApplicationSource } from 'utils/isInWebView';
import { postMessageToApp } from 'utils/postMessageToApp';
import { useSeon } from 'hooks/useSeon';
import { useTranslation } from 'react-i18next';
import classes from 'classnames';
import layout from 'scss/variables.module.scss';
import React, { useEffect, useState } from 'react';
import styles from './BestOffer.module.scss';

const MAX_ATTEMPTS = 3;

export default function BestOffer() {
  const i18Next = useTranslation('bestOffer');
  const [isLegalTermsOpen, setIsLegalTermsOpen] = useState(false);
  const [attemptLoadingData, setAttemptLoadingData] = useState(1);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const { fetch: fetchOffer } = useClientOffer();
  const { fetch: fetchGetApplicationProposals } =
    useGetClientApplicationProposals();
  const { mutateAsync: createApplication } =
    useApplicationAndLexisNexisMutation();
  const { fetch: fetchPutApplication } = usePutApplication();
  const ioBlackBox = useAtomValue(ioBlackBoxAtom);
  const navigate = useNavigate();
  const { setError } = useErrorManager();
  const { sendSeonFraudInfo } = useSeon();
  const { sendAnalyticsCookiesIfExists } = useAnalyticsCookies();
  const { data: application, fetch: fetchApplication } = useApplication();

  const [offerData, setBestOfferData] = useState<
    ResponseOffer | undefined | null
  >();

  const maxAttemptDone = attemptLoadingData > MAX_ATTEMPTS;

  useEffect(() => {
    const nextAttempts = attemptLoadingData + 1;

    const getProposals = async () => {
      try {
        const applicationData =
          application ||
          (await fetchApplication({
            headers: getFetchHeaders({
              Accept: 'application/vnd.4finance.web.v1.hal+json',
              'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
            }),
          }));

        const proposals = await fetchGetApplicationProposals();

        const amount = proposals?.bestOfferProposal?.amount;
        const term = proposals?.bestOfferProposal?.term;

        if (!applicationData?.productInfo.number || !amount || !term)
          throw new Error('best_offer_error');

        const nextBestOfferData = await fetchOffer({
          pathParameters: {
            productNumber: applicationData.productInfo.number,
          },
          queryParameters: {
            amount: amount,
            term: term,
          },
        });
        setBestOfferData(nextBestOfferData);
      } catch (error) {
        setAttemptLoadingData(nextAttempts);
        if (attemptLoadingData === MAX_ATTEMPTS) {
          setError(error);
        }
      }
    };

    if (!maxAttemptDone) {
      const delay = attemptLoadingData === 1 ? 0 : 5000; // delay 5 seconds between calls
      setTimeout(() => getProposals(), delay);
    }
  }, [attemptLoadingData]);

  const OfferWithNoInterests = !Boolean(offerData?.totalInterest) || true;

  const openLegalTermsModal = () => setIsLegalTermsOpen(true);

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true);

      if (offerData?.totalPrincipal && offerData?.term) {
        const applicationCreated = await createApplication({
          amount: offerData?.totalPrincipal,
          term: offerData?.term,
          productNumber: application?.productInfo.number,
          source: getApplicationSource(),
          ioBlackBox,
        });
        postMessageToApp('application_first_main', {
          amount: offerData?.totalPrincipal,
          term: offerData?.term,
          productNumber: application?.productInfo.number,
          source: getApplicationSource(),
        });

        await sendAnalyticsCookiesIfExists();

        try {
          await sendSeonFraudInfo();
        } catch {}

        if (!applicationCreated?.rejected) {
          await fetchPutApplication({
            body: {
              ioBlackBox,
            },
          });
          await fetchApplication({
            headers: getFetchHeaders({
              Accept: 'application/vnd.4finance.web.v1.hal+json',
              'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
            }),
          });
        }
      }
    } catch (e) {
      setError(e);
    } finally {
      setLoadingSubmit(false);
      navigate(ROUTES.FIRST_LOAN_ONLINE_BANK_CHECK); // Same screen as we have on tink redirection
    }
  };

  const term = !isProductKind(application, PRODUCT_NUMBER_IL)
    ? i18Next.t('common:days')
    : i18Next.t('common:months');

  if (attemptLoadingData <= 1 && !offerData) return <Loader />; // Initial loading

  const content = getScreenTranslation('bestOffer', i18Next);

  const loadingData = !offerData && !maxAttemptDone;
  return (
    <Frame
      {...frameConfig}
      sectionID={AppRegistrationSection.Summary}
      hideEditButton={true}
      bodyWrapperClassName={classes(
        layout['frame-x-compact'],
        layout['width-lg'],
      )}
    >
      <div className={styles.layout}>
        <div className={styles.title} data-test="title">
          {content.title}
        </div>
        <div className={styles.description} data-test="description">
          {content.description}
        </div>
        <Card className={styles.card} dataTest="bestOffer-card">
          <div
            className={classes([
              styles['card-header'],
              styles['amount-no-interests'],
            ])}
          >
            <div className={styles['amount-wrapper']}>
              <Placeholder
                className={classes(styles.amount, styles['green-color'])}
                dataTest="amount-principal"
                digits={3}
                value={offerData?.totalPrincipal}
                loading={loadingData}
              />
              <Text
                className={classes(styles.currency, styles['green-color'])}
                dataTest="amount-currency"
                text={content.currency}
                weight="medium"
              />
            </div>
          </div>
          <div className={styles.separator}></div>
          <div className={styles['card-body']}>
            <div className={styles['card-body-layout']}>
              <div className={styles.row}>
                <div data-test="bestOffer-card-table-row1-text">
                  {content.card.table.row1}
                </div>
                <div data-test="bestOffer-card-table-row1-value">
                  <Placeholder
                    className={styles['table-term-value']}
                    dataTest="bestOffer-card-table-row1-value-number"
                    value={offerData?.term}
                    loading={loadingData}
                  />
                  <span>{i18Next.t('card.table.row1TermValue', { term })}</span>
                </div>
              </div>
              <div
                className={classes([styles.separator, styles['no-mobile']])}
              ></div>
              <div className={styles.row} data-test="bestOffer-card-table-row2">
                <div data-test="bestOffer-card-table-row2-text">
                  {content.card.table.row2}
                </div>
                <Placeholder
                  className={styles['table-term-value']}
                  dataTest="bestOffer-card-table-row2-value"
                  value={offerData?.totalPrincipal}
                  parseValue={numberToCurrency}
                  loading={loadingData}
                />
              </div>
              <div
                className={classes([styles.separator, styles['no-mobile']])}
              ></div>
            </div>
            <div
              className={classes([
                styles.row,
                styles.row3,
                OfferWithNoInterests && styles['row-no-interests'],
              ])}
              data-test="bestOffer-card-table-row3"
            >
              <div data-test="bestOffer-card-table-row3-text">
                {content.card.table.row3}
              </div>
              <Placeholder
                dataTest="bestOffer-card-table-row3-value"
                value={offerData?.totalInterest}
                parseValue={numberToCurrency}
                loading={loadingData}
              />
            </div>
            <div
              className={classes([
                styles.separator,
                styles['no-mobile'],
                styles.row3,
              ])}
            ></div>
            <div className={styles['card-body-layout']}>
              <div className={styles.row} data-test="bestOffer-card-table-row4">
                <div data-test="bestOffer-card-table-row4-text">
                  {content.card.table.row4}
                </div>
                <Placeholder
                  dataTest="bestOffer-card-table-row4-value"
                  value={offerData?.totalRepayableAmount}
                  parseValue={numberToCurrency}
                  loading={loadingData}
                />
              </div>
              <div
                className={classes([styles.separator, styles['no-mobile']])}
              ></div>
              <div className={styles.row} data-test="bestOffer-card-table-row5">
                <div data-test="bestOffer-card-table-row5-text">
                  {content.card.table.row5}
                </div>
                <Placeholder
                  dataTest="bestOffer-card-table-row5-value"
                  value={offerData?.agreementEndDate}
                  parseDate={dateFormat}
                  loading={loadingData}
                />
              </div>
            </div>
          </div>
        </Card>
        <Link
          className={styles['legal-terms-link']}
          dataTest="terms-link"
          disabled={isLegalTermsOpen}
          href="#"
          onClick={openLegalTermsModal}
        >
          {content.consents_link}
        </Link>
        <Button
          className={styles.button}
          disabled={!offerData}
          onClick={handleSubmit}
          loading={loadingSubmit}
          dataTest="bestOffer-submit-btn"
        >
          {content.submitBtn}
        </Button>
        <div className={styles.footer}>
          <div className={styles['footer-title']} data-test="footer-title">
            {content.footer.title}
          </div>
          <div
            className={styles['footer-body']}
            dangerouslySetInnerHTML={{
              __html: content.footer.description,
            }}
            data-test="footer-description"
          ></div>
        </div>
      </div>
      {isLegalTermsOpen && (
        <LegalTermsModal
          onClose={() => setIsLegalTermsOpen(false)}
          amount={offerData?.totalPrincipal || 280}
          term={offerData?.term || 30}
        />
      )}
    </Frame>
  );
}
